import { cellToLatLng, cellToParent, getResolution, greatCircleDistance, UNITS } from "h3-js";
import { cellsWithinRadius, radiusDisplayRes } from "../../map/home-radius.js";
import { agingOpacity, isWorsening } from "./incident-presentation.js";
import { displayBucket } from "./display-bucket.js";

export const W_THRESH = 3.0; // corroboration-math v0 confirmation threshold

export interface IncidentView {
  subject: string;
  hazard: string;
  /** Raw CAP event string from the classification (F0b). */
  event?: string;
  /** Raw CAP category string from the classification (F0b). */
  category?: string;
  /** CAP severity string from the classification (F0c); e.g. "Extreme", "Severe", "Moderate", "Minor". */
  capSeverity?: string;
  status: string;
  W: number;
  confidence: number;
  cells: string[];
  distinctIdentities: number;
  cell: string;
  createdAtIso: string;
  /** ISO; stable first-open anchor (payload.openedAt). Unlike createdAtIso this does
   *  NOT move when the snapshot is re-broadcast, so "first seen" stays anchored.
   *  Optional: toView always populates it (falling back to createdAt); synthetic
   *  views built elsewhere may omit it, in which case display falls back to createdAtIso. */
  firstSeenIso?: string;
  /** Provenance of the incident: "crowd" (community reports) or "authority" (official). */
  source?: string;
  /** ISO; freshest report time in the live window (display-clock domain). */
  lastConfirmedAt?: string;
  /** current live-window fire radiative power (MW); absent when no satellite reports. */
  currentFrpMw?: number;
  /** monotonic all-time peak FRP (MW). */
  peakFrpMw?: number;
  /** per-axis change vs the previous snapshot; worsening = any axis rising. */
  trend?: { intensity: string; footprint: string; corroboration: string; worsening: boolean };
  /** T3 attribution: datasource feed and/or authority identity. `itemId` (datasource
   *  source-record id, e.g. NINA/DWD CAP message id) backs a deep link to the warning. */
  contributors?: { feed?: string; identity?: string; itemId?: string }[];
  /** Coarse covering cells of an area warning; matched by containment + rendered as a region. */
  footprint?: string[];
}

function toView(json: Record<string, unknown>): IncidentView {
  const p = (json.payload ?? {}) as Record<string, unknown>;
  const geo = (json.geo ?? {}) as { cell?: string };
  const cells = (p.cells as string[]) ?? [];
  const cls = (p.classification ?? {}) as { category?: string; event?: string; severity?: string };
  const hazard: string = displayBucket(cls.category, cls.event); // bucket; F0a shim removed
  return {
    subject: p.subject as string,
    hazard,
    event: cls.event,
    category: cls.category,
    capSeverity: cls.severity,
    status: p.status as string,
    W: p.W as number,
    confidence: p.confidence as number,
    cells,
    distinctIdentities: (p.distinctIdentities as number) ?? 0,
    cell: geo.cell ?? cells[0] ?? "",
    createdAtIso: (json.createdAt as string) ?? "",
    firstSeenIso: (p.openedAt as string) || (json.createdAt as string) || "",
    source: (p.source as string) ?? "crowd",
    lastConfirmedAt: p.lastConfirmedAt as string | undefined,
    currentFrpMw: p.currentFrpMw as number | undefined,
    peakFrpMw: p.peakFrpMw as number | undefined,
    trend: p.trend as IncidentView["trend"] | undefined,
    contributors: p.contributors as IncidentView["contributors"] | undefined,
    footprint: p.footprint as string[] | undefined,
  };
}

/** Signature of the display-relevant fields of a view. Two snapshots with the same
 *  signature render identically, so a re-broadcast that doesn't move any of these can
 *  skip the (expensive) Now re-render — only time-driven aging (the 30s tick) differs. */
function displaySig(v: IncidentView): string {
  return [
    v.hazard, v.status, v.cell, v.W, v.confidence,
    v.trend?.worsening ? 1 : 0, v.createdAtIso, v.lastConfirmedAt ?? "", v.distinctIdentities,
  ].join("|");
}

/** Latest-wins fold over incident.snapshot, keyed by subject; closed incidents drop out. */
export class IncidentStore {
  #bySubject = new Map<string, { seq: number; view: IncidentView }>();
  /** Fold a snapshot. Returns true when the materialized display CHANGED (new incident,
   *  or a moved display field) — callers gate re-renders on it so heartbeat re-broadcasts
   *  that don't change anything don't rebuild the Now tab every second. */
  ingest(evt: { seq: number; json: Record<string, unknown> }): boolean {
    if (evt.json?.["$type"] !== "app.aid.incident.snapshot") return false;
    const view = toView(evt.json);
    if (!view.subject) return false;
    const prev = this.#bySubject.get(view.subject);
    if (prev && prev.seq > evt.seq) return false; // stale / out-of-order
    this.#bySubject.set(view.subject, { seq: evt.seq, view });
    return !prev || displaySig(prev.view) !== displaySig(view);
  }
  list(): IncidentView[] {
    return [...this.#bySubject.values()].map((e) => e.view).filter((v) => v.status !== "closed");
  }
}

export interface IncidentMarkerFC {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    geometry: { type: "Point"; coordinates: [number, number] };
    properties: { subject: string; status: string; hazard: string; opacity: number; worsening: boolean };
  }>;
}

/** GeoJSON points (one per incident, at its representative cell center, in
 *  [lng,lat] order) for the Now-tab incident marker layer. Skips cell-less views.
 *  `opacity` fades stale incidents (E9), `worsening` flags rising ones for prominence. */
export function incidentMarkersFC(views: IncidentView[], nowMs: number): IncidentMarkerFC {
  return {
    type: "FeatureCollection",
    features: views.filter((v) => v.cell).map((v) => {
      const [lat, lng] = cellToLatLng(v.cell);
      return {
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: [lng, lat] as [number, number] },
        properties: { subject: v.subject, status: v.status, hazard: v.hazard, opacity: agingOpacity(v, nowMs), worsening: isWorsening(v) },
      };
    }),
  };
}

export function cellDistanceKm(a: string, b: string): number {
  const [la, lo] = cellToLatLng(a);
  const [lb, lob] = cellToLatLng(b);
  return greatCircleDistance([la, lo], [lb, lob], UNITS.km);
}

/** True when `homeCell` descends from (or equals) any coarse footprint cell — i.e. the
 *  user's location is inside the warned area. Resolution-independent (works at any radius). */
export function inFootprint(homeCell: string, footprint?: string[]): boolean {
  if (!homeCell || !footprint || footprint.length === 0) return false;
  const homeRes = getResolution(homeCell);
  for (const fc of footprint) {
    const r = getResolution(fc);
    if (r <= homeRes && cellToParent(homeCell, r) === fc) return true;
  }
  return false;
}

export type StatusLevel = "allClear" | "stayAware" | "nearby";

/** Sort incidents by distance from home and classify the home-radius status. */
export function homeStatus(
  views: IncidentView[],
  home: { cell: string; radiusKm: number },
  _nowMicros: number,
): { level: StatusLevel; nearest?: IncidentView; inRadius: Array<{ view: IncidentView; distanceKm: number }> } {
  // Classify by membership in the SAME painted watch cells the user sees on the map
  // (display-resolution hexes whose center is within radiusKm), not an exact distance
  // circle. The painted hexes bulge ~0.5 km past the true radius, so an exact-distance
  // test would call a fire shown inside a tinted hex "all clear" — the map and the Now
  // verdict would disagree at the boundary. Distance is still computed for display/sort.
  const [hlat, hlng] = cellToLatLng(home.cell);
  const res = radiusDisplayRes(home.radiusKm);
  const watch = new Set(cellsWithinRadius(hlat, hlng, home.radiusKm));
  const inWatch = (cell: string): boolean => {
    if (!cell) return false;
    // Incident cells are finer (res-8) than the watch display res (5–7): match by parent.
    // Guard the unexpected coarse-cell case by falling back to the exact-distance test.
    if (getResolution(cell) >= res) return watch.has(cellToParent(cell, res));
    return cellDistanceKm(home.cell, cell) <= home.radiusKm;
  };
  const inRadius = views
    .map((v) => ({ view: v, distanceKm: cellDistanceKm(home.cell, v.cell) }))
    .filter((x) => inWatch(x.view.cell) || inFootprint(home.cell, x.view.footprint))
    .sort((a, b) => a.distanceKm - b.distanceKm);
  const confirmed = inRadius.find((x) => x.view.W >= W_THRESH);
  if (confirmed) return { level: "nearby", nearest: confirmed.view, inRadius };
  if (inRadius.length > 0) return { level: "stayAware", nearest: inRadius[0].view, inRadius };
  return { level: "allClear", inRadius };
}
